import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Modal, Space, Spin, Tooltip } from "antd";
import { FileOutlined, FilePdfOutlined, LeftOutlined, PaperClipOutlined, PlayCircleFilled, RightOutlined } from "@ant-design/icons";
import VideoPlayer from "../../../Components/VideoPlayer";
import VisorImagen from "../../../Components/VisorImagen";
import { downloadFilesAsZip } from "../../../utils/downloadHelpers";
import { buildFlujoDownloadUrl, listarFlujoArchivos, type Flujo, type FlujoFile } from "../flujos.api";
import { idParaAdjuntos } from "../adjuntosFlujo";
import FlujoFilesManager from "./FlujoFilesManager";
import "./AdjuntosEnFila.css";

type Entry = Partial<FlujoFile> | string;

function normalizar(entry: Entry, i: number): FlujoFile | null {
  const data = typeof entry === "string" ? { url: entry } : entry;
  if (!data?.url) return null;
  const mime = data.mimeType || "";
  const kind = data.kind || (mime.startsWith("video/") ? "video" : mime && !mime.startsWith("image/") ? "doc" : /\.(mp4|webm|mov)(?:[?#]|$)/i.test(data.url) ? "video" : "image");
  return { provider: "local", key: data.key || data.url || String(i), thumbKey: data.thumbKey || null,
    url: data.url, thumbUrl: data.thumbUrl || null, originalName: data.originalName || `Archivo ${i + 1}`,
    mimeType: mime, size: data.size || 0, kind, previewUrl: data.previewUrl || null, previewSize: data.previewSize };
}

function reunir(lists: Entry[][]): FlujoFile[] {
  const seen = new Set<string>();
  return lists.flat().map(normalizar).filter((f): f is FlujoFile => {
    if (!f || seen.has(f.key)) return false;
    seen.add(f.key);
    return true;
  });
}

export function Miniatura({ file }: { file: FlujoFile }) {
  const [fallo, setFallo] = useState(false);
  useEffect(() => { setFallo(false); }, [file.url, file.thumbUrl]);
  return <span className="flujo-adjunto-mini">
    {file.kind !== "doc" && !fallo && (file.thumbUrl || file.kind === "image") ?
      <img src={file.thumbUrl || file.url} alt="" loading="lazy" onError={() => setFallo(true)} /> :
      <span className="flujo-adjunto-icon">{file.mimeType === "application/pdf" || /\.pdf$/i.test(file.originalName) ? <FilePdfOutlined /> : <FileOutlined />}</span>}
    {file.kind === "video" && <PlayCircleFilled className="flujo-adjunto-play" />}
  </span>;
}

export function ImagenVista({ file, onZoomChange, onSwipe }: { file: FlujoFile; onZoomChange?: (zoom: number) => void; onSwipe?: (direccion: number) => void }) {
  const [fallo, setFallo] = useState(false);
  if (fallo) return <Alert type="error" showIcon message="No se pudo cargar la imagen" description="El archivo puede no estar disponible o el enlace puede haber vencido. Cierra y vuelve a abrir la galería para reintentar." />;
  return <VisorImagen src={file.url} alt={file.originalName} onError={() => setFallo(true)} onZoomChange={onZoomChange} onSwipe={onSwipe} />;
}

export function Pdf({ file }: { file: FlujoFile }) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = "";
    setUrl(""); setError(false);
    fetch(file.url, { credentials: "include", signal: controller.signal }).then((r) => {
      if (!r.ok) throw new Error("PDF no disponible");
      return r.blob();
    }).then((blob) => { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl); }).catch(() => {
      if (!controller.signal.aborted) setError(true);
    });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [file.url]);
  if (error) return <Alert type="error" showIcon message="No se pudo abrir el PDF" description="Prueba descargarlo o volver a abrir la galería." />;
  return url ? <iframe className="flujo-adjunto-pdf" src={url} title={file.originalName} /> : <Spin tip="Cargando PDF"><div style={{ height: 180 }} /></Spin>;
}

export default function AdjuntosEnFila({ flujo, par, onChanged }: { flujo: Flujo; par?: Flujo | null; onChanged: () => void }) {
  const inicial = useMemo(() => reunir([flujo.imagenes || [], par?.imagenes || []]), [flujo.imagenes, par?.imagenes]);
  const [files, setFiles] = useState<FlujoFile[]>(inicial);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [lado, setLado] = useState<number | string | null>(null);
  const inicioToque = useRef<number | null>(null);
  const destino = idParaAdjuntos(flujo, par);
  useEffect(() => { if (!open) setFiles(inicial); }, [inicial, open]);
  const cargar = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const ids = [...new Set([flujo.id, par?.id].filter((x): x is number => x != null).map(String))];
      const lists = await Promise.all(ids.map((id) => listarFlujoArchivos(id)));
      setFiles(reunir(lists));
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [flujo.id, par?.id]);
  const abrir = (i = 0) => { setIndex(i); setOpen(true); void cargar(); };
  const mover = (n: number) => setIndex((i) => Math.max(0, Math.min(files.length - 1, i + n)));
  useEffect(() => {
    if (!open || manage) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIndex((i) => Math.min(files.length - 1, i + 1));
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [open, manage, files.length]);
  const file = files[Math.min(index, files.length - 1)];
  useEffect(() => { setZoom(1); }, [index, file?.key]);
  const cerrarGestion = () => { setManage(false); void cargar(); onChanged(); };
  return <>
    {inicial.length ? <div className="flujo-adjuntos-fila">
      {inicial.slice(0, 3).map((f, i) => <Tooltip key={f.key} title={f.originalName}><button type="button" className="flujo-adjunto-boton" onClick={() => abrir(i)} aria-label={`Abrir ${f.originalName}`}><Miniatura file={f} /></button></Tooltip>)}
      {inicial.length > 3 && <Button size="small" onClick={() => abrir(3)}>+{inicial.length - 3}</Button>}
    </div> : <Button size="small" icon={<PaperClipOutlined />} onClick={() => { setOpen(true); setManage(true); }}>Adjuntar</Button>}
    <Modal open={open} title="Archivos del movimiento" width="min(96vw, 1080px)" footer={null} onCancel={() => { if (manage) onChanged(); setOpen(false); setManage(false); }} destroyOnClose>
      {manage ? <><Button onClick={cerrarGestion} style={{ marginBottom: 16 }}>Volver a la galería</Button>
        {par && flujo.imagenes?.length && par.imagenes?.length ? <div style={{ marginBottom: 12 }}><p>Este intermediario tiene archivos en ambos movimientos. Elige cuál administrar:</p><Space><Button type={(lado ?? destino) === flujo.id ? "primary" : "default"} onClick={() => setLado(flujo.id)}>Este movimiento</Button><Button type={(lado ?? destino) === par.id ? "primary" : "default"} onClick={() => setLado(par.id)}>Movimiento vinculado</Button></Space></div> : null}
        <FlujoFilesManager flujoId={lado ?? destino} modalOpen={open && manage} /></> : <>
        <div className="flujo-adjunto-toolbar"><Space wrap><Button icon={<PaperClipOutlined />} onClick={() => setManage(true)}>Gestionar adjuntos</Button>
          {files.length > 1 && <Button onClick={() => void downloadFilesAsZip(files.map((f) => ({ url: buildFlujoDownloadUrl(f), name: f.originalName })), `flujo-${flujo.id}-adjuntos`)}>Descargar todos</Button>}</Space>
          {file && <span>{Math.min(index + 1, files.length)} de {files.length} · {file.originalName}</span>}</div>
        {loading ? <Spin tip="Cargando archivos"><div style={{ height: 180 }} /></Spin> : error ? <Alert type="error" showIcon message="No se pudieron cargar los adjuntos" action={<Button onClick={() => void cargar()}>Reintentar</Button>} /> : !file ? <p>Este movimiento aún no tiene archivos adjuntos.</p> : <>
          <div className="flujo-adjunto-visor" onTouchStart={(e) => { inicioToque.current = e.touches[0]?.clientX ?? null; }} onTouchEnd={(e) => { if (inicioToque.current == null || zoom > 1) return; const delta = e.changedTouches[0].clientX - inicioToque.current; if (Math.abs(delta) > 60) mover(delta < 0 ? 1 : -1); inicioToque.current = null; }}>
            <Button aria-label="Archivo anterior" disabled={index <= 0} icon={<LeftOutlined />} onClick={() => mover(-1)} />
            <div className="flujo-adjunto-contenido" key={file.key}>
              {file.kind === "image" ? <ImagenVista file={file} onZoomChange={setZoom} onSwipe={mover} /> : file.kind === "video" ? <VideoPlayer src={file.url} previewSrc={file.previewUrl} nombreArchivo={file.originalName} altoMaximo="60dvh" playsInline /> : file.mimeType === "application/pdf" || /\.pdf$/i.test(file.originalName) ? <Pdf file={file} /> : <div className="flujo-adjunto-documento"><FileOutlined /><p>{file.originalName}</p><p>Descarga el archivo para abrirlo.</p></div>}
            </div>
            <Button aria-label="Archivo siguiente" disabled={index >= files.length - 1} icon={<RightOutlined />} onClick={() => mover(1)} />
          </div>
          <div className="flujo-adjunto-pie"><div className="flujo-adjunto-tira">{files.map((f, i) => <button key={f.key} type="button" onClick={() => setIndex(i)} className={`flujo-adjunto-boton ${i === index ? "activo" : ""}`} aria-label={`Ver ${f.originalName}`}><Miniatura file={f} /></button>)}</div><Button href={buildFlujoDownloadUrl(file)} target="_blank" rel="noopener noreferrer">Descargar</Button></div>
        </>}
      </>}
    </Modal>
  </>;
}
